import React, { useState, useEffect, useCallback, useRef } from 'react';
import mapboxgl from 'mapbox-gl'
import { DataFeature, SourceForMap, dataForSource } from './map_settings';

import './map.css';

function MapComponent({ type, action, actionLabel, elementStylePosition, geoLocation, extraInfo, garagesOnMap, addClass }) {
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [button, setButton] = useState();
  const [mapZoom, setMapZoom] = useState(null);
  const garageMarkersRef = useRef([]);
  const defaultCoordinates = { longitude: -73.97550875532467, latitude: 40.78447898127882 };
  const pointCoordinates = extraInfo?.coordinates || defaultCoordinates;

  const clearGarageMarkers = useCallback(() => {
    garageMarkersRef.current.forEach((mapMarker) => {
      mapMarker.remove();
    });
    garageMarkersRef.current = [];
  }, []);

  const buildDisplayGarages = useCallback((data) => {
    const garages = Object.entries(data || {}).map(([garageID, garage]) => {
      const longitude = parseFloat(garage?.coordinates?.longitude);
      const latitude = parseFloat(garage?.coordinates?.latitude);

      return {
        garageID,
        garage,
        longitude,
        latitude
      };
    });

    if (type !== 'multiple_pointers' || !map) {
      return garages.map(({ garageID, garage, longitude, latitude }) => ({
        garageID,
        garage,
        displayLongitude: !isNaN(longitude) ? longitude : garage.coordinates.longitude,
        displayLatitude: !isNaN(latitude) ? latitude : garage.coordinates.latitude
      }));
    }

    const validGarages = garages.filter(({ longitude, latitude }) => !isNaN(longitude) && !isNaN(latitude));
    const projectedPoints = validGarages.map((item) => ({
      ...item,
      point: map.project([item.longitude, item.latitude])
    }));

    const used = new Set();
    const groupedCoordinates = {};
    const pixelThreshold = 45;
    const zoomSpreadBoost = Math.max(0, 11 - (mapZoom ?? map.getZoom?.() ?? 11)) * 6;

    projectedPoints.forEach((item, index) => {
      if (used.has(item.garageID)) {
        return;
      }

      const group = [item];
      used.add(item.garageID);

      for (let i = index + 1; i < projectedPoints.length; i += 1) {
        const candidate = projectedPoints[i];
        if (used.has(candidate.garageID)) {
          continue;
        }

        const distance = Math.hypot(item.point.x - candidate.point.x, item.point.y - candidate.point.y);
        if (distance <= pixelThreshold) {
          group.push(candidate);
          used.add(candidate.garageID);
        }
      }

      if (group.length === 1) {
        groupedCoordinates[item.garageID] = [item.longitude, item.latitude];
        return;
      }

      const centroid = group.reduce((acc, current) => ({
        x: acc.x + current.point.x,
        y: acc.y + current.point.y
      }), { x: 0, y: 0 });

      centroid.x /= group.length;
      centroid.y /= group.length;

      const spreadRadius = 30 + (group.length * 10) + zoomSpreadBoost;

      group.forEach((current, groupIndex) => {
        const angle = ((Math.PI * 2) / group.length) * groupIndex - (Math.PI / 2);
        const shiftedPoint = {
          x: centroid.x + (Math.cos(angle) * spreadRadius),
          y: centroid.y + (Math.sin(angle) * spreadRadius)
        };
        const shiftedLngLat = map.unproject(shiftedPoint);
        groupedCoordinates[current.garageID] = [shiftedLngLat.lng, shiftedLngLat.lat];
      });
    });

    return garages.map(({ garageID, garage, longitude, latitude }) => {
      const shiftedCoordinates = groupedCoordinates[garageID];
      return {
        garageID,
        garage,
        displayLongitude: shiftedCoordinates ? shiftedCoordinates[0] : (!isNaN(longitude) ? longitude : garage.coordinates.longitude),
        displayLatitude: shiftedCoordinates ? shiftedCoordinates[1] : (!isNaN(latitude) ? latitude : garage.coordinates.latitude)
      };
    });
  }, [map, mapZoom, type])

  const buildFeaturesForSource = useCallback((data) => {
    return buildDisplayGarages(data).map(({ garage, displayLongitude, displayLatitude }) => {
      return DataFeature(displayLongitude, displayLatitude, garage.address, garage.secondLine, garage.imageUrl, garage.rotateA, garage.PageLink);
    });
  }, [buildDisplayGarages])

  const fitMapToGarages = useCallback((mapInstance, garages) => {
    const garageItems = Object.values(garages || {});
    const bounds = new mapboxgl.LngLatBounds();
    let validCount = 0;

    garageItems.forEach((garage) => {
      const longitude = parseFloat(garage?.coordinates?.longitude);
      const latitude = parseFloat(garage?.coordinates?.latitude);

      if (!isNaN(longitude) && !isNaN(latitude)) {
        bounds.extend([longitude, latitude]);
        validCount += 1;
      }
    });

    if (validCount > 1) {
      mapInstance.fitBounds(bounds, { padding: 100 });
    } else if (validCount === 1) {
      mapInstance.fitBounds(bounds, { padding: 250, maxZoom: 14 });
    }
  }, []);


  useEffect(() => {
    if (geoLocation) {
      if (map) {
        map.easeTo({
          duration: 1000,
          center: [geoLocation.longitude, geoLocation.latitude],
          animate: false,
          essential: true
        });

        if (marker) {
          marker.setLngLat([geoLocation.longitude, geoLocation.latitude]);
        }
      }
    }
  }, [geoLocation, map, marker]);

  useEffect(() => {
    if (!map || !marker || !extraInfo) {
      return;
    }

    if ((type === 'point' || type === 'position_selector_edit') && extraInfo.coordinates?.longitude) {
      marker.setLngLat([extraInfo.coordinates.longitude, extraInfo.coordinates.latitude]);
      map.setCenter([extraInfo.coordinates.longitude, extraInfo.coordinates.latitude]);
    }
  }, [extraInfo, map, marker, type]);

  useEffect(() => {
    if (map) {
      if (garagesOnMap) {
        if (type === 'multiple_pointers') {
          clearGarageMarkers();

          if (map.getLayer('points')) {
            map.removeLayer('points');
          }
          if (map.getSource('garages')) {
            map.removeSource('garages');
          }

          const displayGarages = buildDisplayGarages(garagesOnMap);

          displayGarages.forEach(({ garageID, garage, displayLongitude, displayLatitude }) => {
            const markerElement = document.createElement('button');
            markerElement.type = 'button';
            markerElement.className = 'garage-map-marker';
            markerElement.setAttribute('aria-label', garage.address || 'Garage location');
            markerElement.style.backgroundImage = `url('${process.env.PUBLIC_URL}/assets/images/garageIcon.png')`;

            const popup = new mapboxgl.Popup({ offset: 22 }).setHTML(
              `<a target="blank" href=garages-details/${garage.PageLink}>
                <img src='${garage.imageUrl}'
                 style='width:100%;height:200px;transform:rotate(${garage.rotateA}deg)'>
                <h5 class="text-primary text-center">${garage.address}</h5>
                <span>${garage.secondLine}</span>`
            );

            const mapMarker = new mapboxgl.Marker({
              element: markerElement,
              anchor: 'bottom'
            })
              .setLngLat([displayLongitude, displayLatitude])
              .setPopup(popup)
              .addTo(map);

            garageMarkersRef.current.push(mapMarker);
          });

          fitMapToGarages(map, garagesOnMap);
          return;
        }

        const newData = dataForSource();
        const features = buildFeaturesForSource(garagesOnMap);

        newData.features = features;

        if (map.hasImage('garage')) {
          const sourse = map.getSource('garages');
          console.log('Source', sourse);
          console.log('NewData', JSON.stringify(newData, null, 2));

          if (sourse) {
            sourse.setData(newData);
          } else {
            const source = SourceForMap();
            source.data = newData;
            map.addSource('garages', source);
          }

          if (!map.getLayer('points')) {
            map.addLayer({
              'id': 'points',
              'type': 'symbol',
              'source': 'garages',
              'layout': {
                'icon-image': 'garage',
                'icon-size': 1,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true
              }
            });
          }

          fitMapToGarages(map, garagesOnMap);

        } else {
          map.loadImage('/assets/images/garageIcon.png', (error, image) => {
            if (error) {
              console.error('Failed to load garage icon', error);
              return;
            }

            map.addImage('garage', image);

            const source = SourceForMap();
            source.data = newData;

            map.addSource('garages', source);

            map.addLayer({
              'id': 'points',
              'type': 'symbol',
              'source': 'garages',
              'layout': {
                'icon-image': 'garage',
                'icon-size': 1,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true
              }
            });

            // Add popup on click
            map.on('click', 'points', (e) => {
              const coordinates = e.features[0].geometry.coordinates.slice();
              const properties = e.features[0].properties;
              // Create popup
              new mapboxgl.Popup()
                .setLngLat(coordinates)
                .setHTML(`<a target="blank" href=garages-details/${properties.PageLink}>
                <img src='${properties.imageUrl}'
                 style='width:100%;height:200px;transform:rotate(${properties.rotateA}deg)'
                 >
                <h5 class="text-primary text-center">${properties.description}</h5>
                <span>${properties.secondLine}</span>`)
                .addTo(map);
            });
            fitMapToGarages(map, garagesOnMap);
            // Change the cursor to a pointer when hovering over the points layer.
            map.on('mouseenter', 'points', () => {
              map.getCanvas().style.cursor = 'pointer';
            });

            // Change it back to a pointer when it leaves.
            map.on('mouseleave', 'points', () => {
              map.getCanvas().style.cursor = '';
            });
          });
        }
      }
    }
  }, [garagesOnMap, map, buildDisplayGarages, buildFeaturesForSource, clearGarageMarkers, fitMapToGarages, type])

  useEffect(() => {
    if (!map || type !== 'multiple_pointers' || !extraInfo) {
      return;
    }

    const syncSearchCircle = () => {
      const circleData = JSON.parse(extraInfo);
      const existingSource = map.getSource('source_circle_500');

      if (existingSource) {
        existingSource.setData(circleData);
      } else {
        map.addSource('source_circle_500', {
          'type': 'geojson',
          'data': circleData
        });

        map.addLayer({
          'id': 'circle500',
          'type': 'fill',
          'source': 'source_circle_500',
          'paint': {
            'fill-color': '#0080ff',
            'fill-opacity': 0.2,
          }
        });

        map.addLayer({
          'id': 'outline',
          'type': 'line',
          'source': 'source_circle_500',
          'layout': {},
          'paint': {
            'line-color': '#000',
            'line-width': 1
          }
        });
      }
    };

    if (map.isStyleLoaded()) {
      syncSearchCircle();
    } else {
      map.once('load', syncSearchCircle);
    }
  }, [extraInfo, map, type]);

  useEffect(() => {
    mapboxgl.accessToken = 'pk.eyJ1IjoiZG5sc2FsYXphciIsImEiOiJjazlhanAzMGgwNmZoM2ZxcjFubmZyNDQ5In0.vWx_BcKUVet4mGXu6dcexA';
    let createdMap = null;

    switch (type) {
      case 'position_selector': {
        const MapSettings = {
          container: 'map',
          style: 'mapbox://styles/mapbox/streets-v11',
          zoom: 11.2,
          minZoom: 0,
          maxZoom: 0,
          center: [-73.97550875532467, 40.78447898127882],
          maxPitch: 60,
          pitch: 0,
          bearing: 0,
        }

        const newMap = new mapboxgl.Map(MapSettings);
        const nav = new mapboxgl.NavigationControl();
        newMap.addControl(nav, 'bottom-right');

        const marker = new mapboxgl.Marker({
          draggable: true,
          color: "#ff0909",
        }).setLngLat([-73.97550875532467, 40.78447898127882])
          .addTo(newMap);

        function handleActionClick() {
          if (action) {
            action(marker.getLngLat());
          } else {
            console.log('Action for map is not defined');
          }
        }

        newMap.on("click", (e) => {
          const coors = e.lngLat;
          marker.setLngLat([coors.lng, coors.lat]);
          console.log('latlng', coors.lng, coors)
        });

        setMarker(() => marker);

        setButton(() => (
          <span className='map__button la la-check-circle' onClick={handleActionClick}></span>
        ));
        setMap(() => newMap);
        createdMap = newMap;
        break;
      }


      case 'no_pointer': {
        const MapSettings = {
          container: 'map',
          style: 'mapbox://styles/mapbox/streets-v11',
          zoom: 6,
          minZoom: 0,
          maxZoom: 0,
          center: [-73.97550875532467, 40.78447898127882],
          maxPitch: 60,
          pitch: 0,
          bearing: 0,
        }

        const newMap = new mapboxgl.Map(MapSettings);
        const nav = new mapboxgl.NavigationControl();
        newMap.addControl(nav, 'bottom-right');

        newMap.on("click", (e) => {
          console.log("coors", e.lngLat)
        });
        setMap(() => newMap);
        createdMap = newMap;
        break;
      }

      case 'regular': {
        break;
      }


      case 'position_selector_edit': {
        const MapSettings = {
          container: 'map',
          style: 'mapbox://styles/mapbox/streets-v11',
          zoom: 12,
          minZoom: 0,
          maxZoom: 0,
          center: [-73.97550875532467, 40.78447898127882],
          maxPitch: 60,
          pitch: 0,
          bearing: 0,
        }
        const newMap = new mapboxgl.Map(MapSettings);
        const nav = new mapboxgl.NavigationControl();
        newMap.addControl(nav, 'bottom-right');

        const marker = new mapboxgl.Marker({
          draggable: true,
          color: "#ff0909",
        }).setLngLat([-73.97550875532467, 40.78447898127882])
          // .setLngLat([extraInfo.coordinates.longitude, extraInfo.coordinates.latitude])
          .addTo(newMap);

        function handleActionClick() {
          if (action) {
            action(marker.getLngLat());
          } else {
            console.log('Action for map is not defined');
          }
        }

        newMap.on("click", (e) => {
          const coors = e.lngLat;
          marker.setLngLat([coors.lng, coors.lat]);
        });


        setMarker(() => marker);
        //console.log(extraInfo)

        setButton(() => (
          <span className='map__button la la-map-marker' onClick={handleActionClick}>{actionLabel ? actionLabel : 'add_task'}</span>
        ));


        if (extraInfo && extraInfo.coordinates && extraInfo.coordinates.longitude) {
          marker.setLngLat([extraInfo.coordinates.longitude, extraInfo.coordinates.latitude]);

          newMap.setCenter([extraInfo.coordinates.longitude, extraInfo.coordinates.latitude]);
        }
        setMap(() => newMap);
        createdMap = newMap;
        break;
      }

      case 'point': {
        console.log('extraInfoextraInfo', extraInfo)
        const MapSettings = {
          container: 'map',
          style: 'mapbox://styles/mapbox/streets-v11',
          zoom: 13,
          center: [pointCoordinates.longitude, pointCoordinates.latitude],
          maxPitch: 60,
          pitch: 0,
          bearing: 0,
        }
        const Multiplepointer = new mapboxgl.Map(MapSettings);

        const marker = new mapboxgl.Marker({
          draggable: false,
          zoom: 9,
          color: "#ff0909",
        }).setLngLat([pointCoordinates.longitude, pointCoordinates.latitude])
          .addTo(Multiplepointer);

        setMarker(() => marker);
        if (extraInfo) {
          setButton(() => (
            <span className='map__button map__address'>{extraInfo.address}</span>
          ));

          marker.setLngLat([pointCoordinates.longitude, pointCoordinates.latitude]);

          Multiplepointer.setCenter([pointCoordinates.longitude, pointCoordinates.latitude]);
        }
        setMap(() => Multiplepointer);
        createdMap = Multiplepointer;
        break;
      }

      case 'multiple_pointers': {
        const MapSettings = {
          container: 'map',
          style: 'mapbox://styles/mapbox/streets-v11',
          zoom: 11,
          center: [-73.97550875532467, 40.78447898127882]
        }

        const Multiplepointer = new mapboxgl.Map(MapSettings);
        Multiplepointer.on('load', () => {
          setMapZoom(Multiplepointer.getZoom());
        });
        Multiplepointer.on('zoomend', () => {
          setMapZoom(Multiplepointer.getZoom());
        });
        setMap(() => Multiplepointer);
        createdMap = Multiplepointer;
        break;

      }
      default:
        break;
    }

    return () => {
      clearGarageMarkers();
      setButton(undefined);
      setMarker(null);
      setMap(null);

      if (createdMap) {
        createdMap.remove();
      }
    };
  }, [action, actionLabel, clearGarageMarkers, pointCoordinates.latitude, pointCoordinates.longitude, type, elementStylePosition]);

  const garagesCount = garagesOnMap ? Object.keys(garagesOnMap).length : 0;

  return (
    <div className={`fullwidth-home-map ${addClass ? addClass : ''}  map__element_container${elementStylePosition ? `--${elementStylePosition}` : ''}`} id="map-container">
      <div id='map' className='map__container'></div>
      {type === 'multiple_pointers' && garagesCount > 0 && (
        <>
          <span className='map__button map__summary'>
            {garagesCount} garage{garagesCount > 1 ? 's' : ''} on map
          </span>
          <span className='map__button map__hint'>
            Click a marker to view details
          </span>
        </>
      )}
      {button && button}
    </div>
  )
}

export default MapComponent;
